import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Sparkles, LayoutDashboard } from 'lucide-react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from '@/hooks/useTheme';
import { AuthProvider } from '@/hooks/useAuth';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireNetwork } from '@/components/RequireNetwork';
import { LoginPage } from '@/modules/auth/LoginPage';
import { ResearchPage } from '@/modules/sales-research/ResearchPage';
import { CompanyDetailPage } from '@/modules/sales-research/CompanyDetailPage';
import { CorporatePage } from '@/modules/sales-corp/CorporatePage';
import { SalesShell } from './SalesShell';
import { ComingSoon } from './ComingSoon';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 },
  },
});

/** Standalone Sales portal — deployed at sales.flaxfoods.in. */
export function SalesApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={200} skipDelayDuration={500}>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  element={
                    <RequireNetwork>
                      <RequireAuth>
                        <SalesShell />
                      </RequireAuth>
                    </RequireNetwork>
                  }
                >
                  <Route index element={<Navigate to="/research" replace />} />
                  <Route path="research" element={<ResearchPage />} />
                  <Route path="research/:id" element={<CompanyDetailPage />} />
                  <Route path="corporate" element={<CorporatePage />} />
                  <Route
                    path="subscription"
                    element={
                      <ComingSoon
                        title="OneLife by Flax"
                        phase="Phase 3"
                        icon={Sparkles}
                        description="Segment explorer, consent-first WhatsApp/email campaigns, conversation inbox and the OneLife funnel."
                      />
                    }
                  />
                  <Route
                    path="dashboard"
                    element={
                      <ComingSoon
                        title="Sales dashboard"
                        phase="Phase 4"
                        icon={LayoutDashboard}
                        description="Pipeline value, conversion by stage, wins, new subscribers and cost-per-win per vertical, city and outlet."
                      />
                    }
                  />
                  <Route path="*" element={<Navigate to="/research" replace />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
